import { getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { doc } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { AI_PIPELINE_SCHEMA_V2 } from '../../../constants/aiPipelineModes';
import { schoolSchemaRef } from './schoolSchemaPaths';

const SCHEMA_VERSION = '1.3';

function entry(value, source = 'registration') {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  return {
    value,
    provenance: { source, at: new Date().toISOString() },
    confidence: source === 'registration' ? 0.9 : 0.5,
  };
}

function setField(fields, id, value, source = 'registration') {
  const e = entry(value, source);
  if (e) fields[id] = e;
}

/**
 * Map schools/{sid} + answers into a shared schema-shaped document.
 * Additive: merges into existing schoolSchemas/{sid} without wiping evidence extracts.
 */
export async function seedSchoolSchemaFromRegistration(schoolId, options = {}) {
  const { merge = true } = options;
  const schoolSnap = await getDoc(doc(db, 'schools', schoolId));
  if (!schoolSnap.exists()) {
    throw new Error('School not found for schema seed');
  }
  const school = schoolSnap.data() || {};
  const answers = school.answers || {};
  const fields = {};

  setField(fields, 'meta.official_name', answers.officialName || school.officialName);
  setField(fields, 'meta.country', answers.officialAddressCountry || answers.country || school.country);
  setField(fields, 'meta.school_type', answers.q4);
  setField(fields, 'meta.school_phase', answers.q4);
  setField(fields, 'meta.curriculum', answers.q6);
  setField(fields, 'meta.instruction_language', answers.q5);
  setField(fields, 'meta.accreditation_level', school.accreditationLevel);
  setField(fields, 'meta.school_id', schoolId, 'system');

  setField(fields, 'demo.total_roll', answers.q7);
  setField(fields, 'demo.age_range', answers.q8);
  setField(fields, 'demo.gender_mix', answers.q9);
  if (answers.q10 != null) setField(fields, 'demo.need_percentage_grid', answers.q10);
  setField(fields, 'demo.inclusion_narrative', answers.q11);

  setField(fields, 'staff.total_headcount', answers.q12);
  if (answers.q13 != null) setField(fields, 'spec.headcount_by_role', answers.q13);
  if (answers.q13?.['Learning Support'] != null) {
    setField(fields, 'staff.lsa_headcount_partial', answers.q13['Learning Support']);
  }
  setField(fields, 'spec.parent_funded_note', answers.q14);
  if (answers.q15 != null) setField(fields, 'spec.roles_present', answers.q15);

  if (answers.schoolExclusions != null) {
    setField(fields, 'impact.school_exclusions', answers.schoolExclusions);
  }

  const ref = schoolSchemaRef(schoolId);
  const existing = merge ? await getDoc(ref) : null;
  const prev = existing?.exists() ? existing.data() : {};
  const prevFields = prev.fields && typeof prev.fields === 'object' ? prev.fields : {};

  // Registration seed overwrites only registration-provenance keys; keep evidence_extract fields.
  const nextFields = { ...prevFields };
  Object.entries(fields).forEach(([id, e]) => {
    const prior = nextFields[id];
    if (prior?.provenance?.source === 'evidence_extract' || prior?.provenance?.source === 'accreditor_note') {
      return;
    }
    nextFields[id] = e;
  });

  const payload = {
    schemaId: 'ifip.accreditation.data',
    schemaVersion: SCHEMA_VERSION,
    pipeline: AI_PIPELINE_SCHEMA_V2,
    schoolId,
    officialName: answers.officialName || school.officialName || null,
    fields: nextFields,
    softEvidence: Array.isArray(prev.softEvidence) ? prev.softEvidence : [],
    claims: Array.isArray(prev.claims) ? prev.claims : [],
    evidenceQuality: prev.evidenceQuality && typeof prev.evidenceQuality === 'object' ? prev.evidenceQuality : {},
    registrationSeededAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(ref, payload, { merge: true });
  return payload;
}